/**
 * Erfassungs-Dialog (F9/F10): Die KI stellt Rückfragen und fasst zusammen
 * („So habe ich dich verstanden …“); der Kunde bestätigt oder korrigiert.
 * Styling nach Design-Handoff (Screens 03/03c).
 */

import { useEffect, useRef, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"
import { api, apiError, apiStatus } from "@/services/api"
import type { ChatNachricht } from "@/types"

interface Props {
  reportId: number
  nachrichten: ChatNachricht[]
  onNeueNachrichten: (nachrichten: ChatNachricht[]) => void
  /** true im Fenster-Modus: Chat nutzt die volle Höhe der Spalte */
  hoch?: boolean
}

export function ErfassungsChat({ reportId, nachrichten, onNeueNachrichten, hoch = false }: Props) {
  const [eingabe, setEingabe] = useState("")
  const [kiVerfuegbar, setKiVerfuegbar] = useState(true)
  const endeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endeRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [nachrichten.length])

  const senden = useMutation({
    mutationFn: (inhalt: string) => api.reports.chat(reportId, inhalt),
    onMutate: (inhalt) => {
      onNeueNachrichten([
        ...nachrichten,
        { id: -Date.now(), rolle: "kunde", inhalt, erstelltAm: new Date().toISOString() },
      ])
      setEingabe("")
    },
    onSuccess: (antwort, inhalt) => {
      onNeueNachrichten([
        ...nachrichten,
        { id: antwort.id - 1, rolle: "kunde", inhalt, erstelltAm: antwort.erstelltAm },
        antwort,
      ])
    },
    onError: (e, inhalt) => {
      // Optimistische Nachricht zurücknehmen
      onNeueNachrichten(nachrichten)
      setEingabe(inhalt)
      // 503 = kein Provider konfiguriert → Chat ausblenden, manueller Weg bleibt
      if (apiStatus(e) === 503) setKiVerfuegbar(false)
      toast.error(apiError(e))
    },
  })

  if (!kiVerfuegbar) {
    return (
      <p className="rounded-lg bg-warning/10 px-3 py-2 text-xs text-warning">
        Die Erfassungs-KI ist derzeit nicht verfügbar — bitte fülle die Felder manuell aus.
      </p>
    )
  }

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-[10px] border ${
        hoch ? "min-h-0 flex-1" : ""
      }`}
    >
      <div className="flex items-center justify-between border-b bg-card px-3.5 py-2.5">
        <span className="text-[13px] font-bold">Erfassungs-Assistent</span>
        <span className="flex items-center gap-1.5 text-xs font-semibold text-success">
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          KI aktiv
        </span>
      </div>
      <div className={`overflow-y-auto bg-muted/40 p-2.5 ${hoch ? "min-h-0 flex-1" : "max-h-64"}`}>
        {nachrichten.length === 0 && (
          <p className="px-1 py-2 text-xs text-muted-foreground">
            Beschreibe kurz dein Anliegen — die KI stellt Rückfragen, bis die Meldung
            eindeutig ist.
          </p>
        )}
        <div className="flex flex-col gap-2">
          {nachrichten.map((n) => (
            <div
              key={n.id}
              className={`max-w-[85%] whitespace-pre-wrap px-3 py-2 text-[13px] leading-normal ${
                n.rolle === "kunde"
                  ? "self-end rounded-[12px_12px_3px_12px] bg-primary text-primary-foreground"
                  : "self-start rounded-[12px_12px_12px_3px] bg-secondary"
              }`}
            >
              {n.inhalt}
            </div>
          ))}
          {senden.isPending && (
            <div className="self-start rounded-[12px_12px_12px_3px] bg-secondary px-3 py-2 text-[13px] text-muted-foreground">
              KI überlegt …
            </div>
          )}
        </div>
        <div ref={endeRef} />
      </div>
      <form
        className="flex gap-2 border-t bg-card p-2"
        onSubmit={(e) => {
          e.preventDefault()
          const inhalt = eingabe.trim()
          if (inhalt && !senden.isPending) senden.mutate(inhalt)
        }}
      >
        <input
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          placeholder="Nachricht an die Erfassungs-KI …"
          className="w-full rounded-lg border px-3 py-1.5 text-[13px]"
        />
        <button
          type="submit"
          disabled={!eingabe.trim() || senden.isPending}
          className="shrink-0 rounded-lg bg-primary px-3.5 py-1.5 text-[13px] font-semibold text-primary-foreground disabled:opacity-50"
        >
          Senden
        </button>
      </form>
    </div>
  )
}
